
import React from 'react';
import { Poll } from '@/types/group';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/auth';
import PollHeader from './PollHeader';
import PollOptionItem from './PollOptionItem';
import PollActions from './PollActions';
import { usePollVoting } from './hooks/usePollVoting';
import { usePollActions } from './hooks/usePollActions';
import { calculateTotalVotes, hasUserVoted, getUserVotedOption } from './utils/pollUtils';

interface PollItemProps {
  poll: Poll;
  onPollDeleted?: (pollId: string) => void;
}

const PollItem: React.FC<PollItemProps> = ({ poll, onPollDeleted }) => {
  const { user } = useAuth();
  const { isVoting, handleVote } = usePollVoting(poll.id);
  const { isDeleting, isDeleted, handleClosePoll, handleDeletePoll } = usePollActions(poll, onPollDeleted);
  
  // Calculations and derived state
  const totalVotes = calculateTotalVotes(poll);
  const hasVoted = hasUserVoted(poll, user?.id);
  const userVotedOption = getUserVotedOption(poll, user?.id);
  const isCreator = user && poll.created_by === user.id;
  
  const handleVoteClick = (optionId: string) => {
    handleVote(optionId, user, hasVoted, userVotedOption);
  };

  const handleDelete = () => {
    handleDeletePoll(user);
  };

  // Don't render the poll if it's deleted
  if (isDeleted) {
    return null;
  }

  return (
    <Card className="h-full">
      <PollHeader 
        question={poll.question}
        isClosed={poll.closed}
        totalVotes={totalVotes}
        createdAt={poll.created_at}
      />
      
      <CardContent>
        <div className="space-y-3">
          {poll.options?.map((option) => (
            <PollOptionItem
              key={option.id}
              option={option}
              totalVotes={totalVotes}
              userId={user?.id}
              isPollClosed={poll.closed}
              isVoting={isVoting}
              onVote={handleVoteClick}
            />
          ))}
        </div>
      </CardContent>
      
      <PollActions
        isCreator={!!isCreator}
        isClosed={poll.closed}
        isDeleting={isDeleting}
        onClose={handleClosePoll}
        onDelete={handleDelete}
      />
    </Card>
  );
};

export default PollItem;
